import { useEffect, useState } from 'react'
import { Loader } from '@googlemaps/js-api-loader'

import useCurrentLocale from '../../../../core/hook/useCurrentLocale'
import { CmsSellerFilters } from '../../../../domain/seller'
import { HTMLInputMapId, RADIUS_TYPE_KM } from '../constants'
import { convertMilesToKm } from '../helper'

import { GoogleMapHTMLSellersMapId } from './constants'
import { GoogleFormData, InitAutoCompleteOptions } from './types'

export const useLoadGoogleMapApi = () => {
  const [isLoaded, setIsLoaded] = useState(false)
  const currentLocale = useCurrentLocale()

  const apiKey = process.env.NEXT_PUBLIC_GOOGLE_MAPS_APIKEY

  if (!apiKey) {
    throw Error('google map api key is missing')
  }

  useEffect(() => {
    new Loader({
      id: 'google-map-script',
      apiKey,
      libraries: ['places'],
      language: currentLocale,
    })
      .load()
      .then(() => {
        setIsLoaded(true)
      })
      .catch(console.error) // eslint-disable-line
  }, [currentLocale])

  return isLoaded
}

export const googleInitAutoComplete = ({
  onChangePlace,
  defautNameValue,
  resultTypes,
}: InitAutoCompleteOptions) => {
  const mapInputCitiesEl = document.querySelector<HTMLInputElement>(`#${HTMLInputMapId}`)

  if (!mapInputCitiesEl) {
    return null
  }

  if (defautNameValue) {
    mapInputCitiesEl.value = defautNameValue
  }

  const autoComplete = new google.maps.places.Autocomplete(mapInputCitiesEl, {
    fields: ['name', 'geometry', 'types', 'formatted_address'],
    types: resultTypes ?? ['locality', 'country'],
  })

  autoComplete.addListener('place_changed', () => {
    const place = autoComplete.getPlace()
    if (!place) {
      return
    }
    onChangePlace(place)
  })

  return autoComplete
}

export const googleTransformFormDataToCmsSellerFilter = (
  formData: GoogleFormData,
): CmsSellerFilters => {
  const filters: CmsSellerFilters = {}

  if (formData.place) {
    if (formData.place.types?.includes('locality')) {
      filters.city = formData.place.name
    }
    if (formData.place.types?.includes('country')) {
      filters.country = formData.place.name
    }

    if (!formData.lat && !formData.lng) {
      if (formData.place.geometry?.location) {
        const valueRadius = formData.radius || 20

        filters.city = undefined
        filters.country = undefined
        filters.radius = {
          lat: formData.place.geometry.location.lat(),
          lng: formData.place.geometry.location.lng(),
          value:
            formData.typeRadius === RADIUS_TYPE_KM ? convertMilesToKm(valueRadius) : valueRadius,
        }
      }
    }
  }

  if (formData.type && formData.type !== 'all') {
    filters.type = formData.type
  }

  if (formData.categories?.length) {
    filters.categories = [...formData.categories]
  }

  return filters
}

export const googleFetchCityFromGeolocationPosition = async (
  geolocationPosition: GeolocationPosition,
) => {
  const { latitude, longitude } = geolocationPosition.coords

  // get city information from lat lng
  const res = await fetch(
    `https://maps.googleapis.com/maps/api/geocode/json?latlng=${latitude},${longitude}&key=${process.env.NEXT_PUBLIC_GOOGLE_MAPS_APIKEY}`,
  )

  const data = await res.json()

  const city = data.results?.find((result) => result.types?.includes('locality'))

  return city as { place_id: string }
}

export const googleGetMapEl = () =>
  document.querySelector<HTMLElement>(`#${GoogleMapHTMLSellersMapId}`)
